/**
 * Aba Documentos & Endereços do funcionário (E10a — integração Convenia):
 * somente leitura nesta fatia — os dados chegam pelo sync Convenia
 * (pessoa_documentos/pessoa_enderecos). Endereço tem vigência AAAAMM.
 */
import { useEffect, useState } from "react";
import { Badge, Card, Group, Table, Text, Title } from "@mantine/core";
import { api } from "../../api";
import { fmtData, fmtCompetencia } from "../../format";
import { Carregando, Vazio } from "../../componentes/Estados";
import { TabelaDados, Th } from "../../componentes/listas";
import { ArquivosCard } from "./ArquivosCard";

interface Documento {
  tipo: string;
  numero: string;
  orgaoEmissor: string | null;
  uf: string | null;
  dataEmissao: string | null;
  dataValidade: string | null;
  complemento: Record<string, unknown> | null;
}
interface Endereco {
  id: string;
  tipo: string;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  cep: string | null;
  uf: string | null;
  cidade: string | null;
  bairro: string | null;
  vigenciaInicio: number;
  vigenciaFim: number | null;
}

const ROTULO_DOC: Record<string, string> = {
  rg: "RG",
  ctps: "CTPS",
  pis: "PIS/PASEP",
  titulo_eleitor: "Título de eleitor",
  cnh: "CNH",
  reservista: "Reservista",
  passaporte: "Passaporte",
};

export function DocumentosTab({ funcionarioId }: { funcionarioId: string }) {
  const [docs, setDocs] = useState<Documento[] | null>(null);
  const [ends, setEnds] = useState<Endereco[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ documentos: Documento[] }>(`/funcionarios/${funcionarioId}/documentos`),
      api.get<{ enderecos: Endereco[] }>(`/funcionarios/${funcionarioId}/enderecos`),
    ])
      .then(([d, e]) => {
        setDocs(d.documentos);
        setEnds(e.enderecos);
      })
      .catch(setErro);
  }, [funcionarioId]);

  if (erro) return <Text c="red.7">{String(erro)}</Text>;
  if (!docs || !ends) return <Carregando linhas={4} />;

  return (
    <>
      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Documentos <Badge variant="light">{docs.length}</Badge>
        </Title>
        {docs.length === 0 ? (
          <Vazio texto="Nenhum documento sincronizado — rode o sync Convenia (domínio documentos) para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Tipo</Th>
                <Th>Número</Th>
                <Th>Emissor/UF</Th>
                <Th>Emissão</Th>
                <Th>Validade</Th>
                <Th>Complemento</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {docs.map((d) => (
                <Table.Tr key={`${d.tipo}-${d.numero}`}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {ROTULO_DOC[d.tipo] ?? d.tipo}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {d.numero}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {[d.orgaoEmissor, d.uf].filter(Boolean).join("/") || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{d.dataEmissao ? fmtData(d.dataEmissao) : "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{d.dataValidade ? fmtData(d.dataValidade) : "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {d.complemento
                        ? Object.entries(d.complemento)
                            .map(([k, v]) => `${k}: ${String(v)}`)
                            .join(" · ")
                        : "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>

      <Card withBorder shadow="xs" mb="md">
        <Title order={5} mb="sm">
          Endereços <Badge variant="light">{ends.length}</Badge>
        </Title>        {ends.length === 0 ? (
          <Vazio texto="Nenhum endereço sincronizado — rode o sync Convenia (domínio endereços) para esta pessoa." />
        ) : (
          <TabelaDados>
            <Table.Thead>
              <Table.Tr>
                <Th>Tipo</Th>
                <Th>Endereço</Th>
                <Th>Cidade/UF</Th>
                <Th>CEP</Th>
                <Th>Vigência</Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ends.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Badge variant="outline" color="gray">
                      {e.tipo}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {[e.logradouro, e.numero, e.complemento, e.bairro].filter(Boolean).join(", ") || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {[e.cidade, e.uf].filter(Boolean).join("/") || "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {e.cep ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {fmtCompetencia(e.vigenciaInicio)}
                      {e.vigenciaFim ? ` → ${fmtCompetencia(e.vigenciaFim)}` : " → vigente"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </TabelaDados>
        )}
      </Card>
      <ArquivosCard funcionarioId={funcionarioId} />
      <Group>
        <Text size="xs" c="dimmed">
          Dados somente leitura — a fonte é o Convenia (sync na tela Integração Convenia).
        </Text>
      </Group>
    </>
  );
}
